<template>
  <figure class="pi-line">
    <figcaption v-if="title" class="pi-line__title">{{ title }}</figcaption>
    <p v-if="chart.empty" class="pi-line__empty">{{ emptyText }}</p>
    <div v-else ref="plot" class="pi-line__plot" tabindex="0" role="slider" :aria-label="ariaLabel"
         aria-valuemin="0" :aria-valuemax="lastIndex" :aria-valuenow="sliderIndex" :aria-valuetext="sliderText"
         @mousemove="onPointer" @mouseleave="clear" @touchstart="onTouch" @touchmove="onTouch"
         @focus="onFocus" @blur="clear" @keydown="onKey">
      <svg ref="svg" class="pi-line__svg" :viewBox="'0 0 ' + chart.width + ' ' + chart.height" role="img"
           :aria-label="ariaLabel" focusable="false">
        <defs>
          <pattern :id="patternId" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" class="pi-line__hatch"/>
          </pattern>
        </defs>
        <g v-if="coverage">
          <rect :x="coverage.x" :y="chart.plot.top" :width="coverage.width" :height="plotHeight"
                :fill="'url(#' + patternId + ')'" class="pi-line__coverage"/>
          <text v-if="coverage.width > 90" :x="coverage.x + 6" :y="chart.plot.top + 12" class="pi-line__coverage-label">{{ coverage.label }}</text>
        </g>
        <g class="pi-line__grid">
          <line v-for="tick in chart.yTicks" :key="'g' + tick.value" :x1="chart.plot.left" :x2="chart.plot.right"
                :y1="tick.y" :y2="tick.y"/>
        </g>
        <g v-if="weekends.length" class="pi-line__weekends" aria-hidden="true">
          <rect v-for="(band, index) in weekends" :key="'w' + index" :x="band.x" :y="chart.plot.top" :width="band.width"
                :height="plotHeight" class="pi-line__weekend"/>
        </g>
        <g class="pi-line__axis">
          <text v-for="tick in chart.yTicks" :key="'y' + tick.value" :x="chart.plot.left - 8" :y="tick.y + 4"
                text-anchor="end">{{ tick.label }}</text>
          <text v-for="(tick, index) in chart.xTicks" :key="'x' + index" :x="tick.x" :y="chart.height - 8"
                text-anchor="middle">{{ tick.label }}</text>
        </g>
        <g v-for="item in chart.series" :key="item.key">
          <path v-if="item.area" :d="item.area" :fill="item.color" class="pi-line__area"/>
          <path :d="item.path" fill="none" :stroke="item.color" :stroke-dasharray="item.dash || null"
                class="pi-line__stroke"/>
          <circle v-for="(dot, index) in item.dots" :key="index" :cx="dot.x" :cy="dot.y" :r="dotRadius"
                  :fill="dot.partial ? '#ffffff' : item.color" :stroke="item.color" class="pi-line__dot"/>
        </g>
        <g v-if="hover" class="pi-line__focus" aria-hidden="true">
          <line :x1="hover.x" :x2="hover.x" :y1="chart.plot.top" :y2="chart.plot.bottom" class="pi-line__guide"/>
          <circle v-for="row in hoverDots" :key="'h' + row.key" :cx="hover.x" :cy="row.y" r="5" :fill="row.color"
                  class="pi-line__focus-dot"/>
        </g>
      </svg>
      <div v-if="hover" ref="tip" class="pi-line__tip" :style="tipStyle" aria-hidden="true">
        <div class="pi-line__tip-head">{{ hover.title }}</div>
        <table class="pi-line__tip-table">
          <thead>
          <tr>
            <th></th>
            <th class="is-num">valor</th>
            <th class="is-num">{{ hover.weekLabel ? 'dia ant.' : 'ant.' }}</th>
            <th v-if="hover.hasWeek" class="is-num">sem. ant.</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in hover.rows" :key="'r' + row.key">
            <td class="pi-line__tip-name">
              <span class="pi-line__tip-swatch" :style="{ background: row.color }"></span>{{ row.label }}
            </td>
            <td class="is-num pi-line__tip-value" :class="{ 'is-muted': row.muted }">{{ row.text }}</td>
            <td class="is-num pi-line__tip-delta" :class="'is-' + row.deltaTone">{{ row.delta || '—' }}</td>
            <td v-if="hover.hasWeek" class="is-num pi-line__tip-delta" :class="'is-' + row.weekDeltaTone">{{ row.weekDelta || '—' }}</td>
          </tr>
          </tbody>
        </table>
        <ul v-if="hover.extras.length" class="pi-line__tip-extras">
          <li v-for="extra in hover.extras" :key="'e' + extra.label"><span class="pi-line__tip-label">{{ extra.label }}:</span> {{ extra.text }}</li>
        </ul>
        <ul v-if="hover.flags.length" class="pi-line__tip-flags">
          <li v-for="flag in hover.flags" :key="'f' + flag.kind" :class="'is-' + flag.kind">{{ flag.text }}</li>
        </ul>
      </div>
    </div>
    <ul v-if="!chart.empty" class="pi-line__legend">
      <li v-for="item in chart.series" :key="'l' + item.key" class="pi-line__legend-item">
        <svg class="pi-line__swatch" width="28" height="10" viewBox="0 0 28 10" aria-hidden="true" focusable="false">
          <line x1="0" y1="5" x2="28" y2="5" :stroke="item.color" :stroke-dasharray="item.dash || null" stroke-width="2"/>
        </svg>
        <span>{{ item.label }}</span>
        <span v-if="item.lastText" class="pi-line__last">último: {{ item.lastText }}</span>
      </li>
      <li v-if="hasPartial" class="pi-line__legend-item pi-line__last">{{ partialLabel }}</li>
      <li v-if="weekends.length" class="pi-line__legend-item pi-line__last">
        <span class="pi-line__key pi-line__key--weekend" aria-hidden="true"></span>fim de semana
      </li>
      <li v-if="coverage" class="pi-line__legend-item pi-line__last">
        <span class="pi-line__key pi-line__key--coverage" aria-hidden="true"></span>antes do início da medição
      </li>
    </ul>
  </figure>
</template>

<script>
import { buildLineChart, responsiveChartSize } from "./piScale.js";
import { coverageBand, hoverModel, hoverSummary, nearestIndex, tooltipPlacement, weekendBands } from "./piChartHover.js";

const RESIZE_DELAY_MS = 150;
const TIP_GAP_PX = 12;

let sequence = 0;

export default {
  name: "pi-line-chart",
  props: {
    series: { type: Array, default: () => [] },
    unit: { type: String, default: "count" },
    title: { type: String, default: "" },
    height: { type: Number, default: 240 },
    emptyText: { type: String, default: "Sem pontos no período." },
    formatValue: { type: Function, default: null },
    xLabelCount: { type: Number, default: 6 },
    formatLabel: { type: Function, default: null },
    partialLabel: { type: String, default: "Ponto vazado = dia parcial" },
    coverageFrom: { type: String, default: "" },
    tooltipExtras: { type: Function, default: null },
    partialFlag: { type: String, default: "" },
  },
  data() {
    sequence += 1;
    return {
      measuredWidth: 0,
      resizeTimer: null,
      hoverIndex: null,
      tipLeft: 0,
      tipTop: null,
      patternId: "pi-line-hatch-" + sequence,
    };
  },
  computed: {
    size() {
      return responsiveChartSize(this.measuredWidth, this.xLabelCount);
    },
    chart() {
      const options = { width: this.size.width, height: this.height, unit: this.unit, xLabelCount: this.size.xLabelCount };
      if (this.formatValue) options.formatValue = this.formatValue;
      if (this.formatLabel) options.formatDate = this.formatLabel;
      return buildLineChart(this.series, options);
    },
    plotHeight() {
      return Math.max(0, this.chart.plot.bottom - this.chart.plot.top);
    },
    weekends() {
      return weekendBands(this.chart, this.coverageFrom);
    },
    coverage() {
      return this.coverageFrom ? coverageBand(this.chart, this.coverageFrom) : null;
    },
    dotRadius() {
      const count = this.chart.series.reduce((max, item) => Math.max(max, item.dots.length), 0);
      return count > 120 ? 1.5 : 3;
    },
    hasPartial() {
      return this.chart.series.some((item) => item.dots.some((dot) => dot.partial));
    },
    hover() {
      if (this.hoverIndex === null) return null;
      const options = { unit: this.unit, coverageFrom: this.coverageFrom, extras: this.tooltipExtras, partialText: this.partialFlag };
      if (this.formatValue) options.formatValue = this.formatValue;
      if (this.formatLabel) options.formatDate = this.formatLabel;
      return hoverModel(this.chart, this.hoverIndex, options);
    },
    hoverDots() {
      return this.hover ? this.hover.rows.filter((row) => row.y !== null && row.y !== undefined) : [];
    },
    tipStyle() {
      return this.tipTop === null ? { left: this.tipLeft + "px" } : { left: this.tipLeft + "px", top: this.tipTop + "px" };
    },
    lastIndex() {
      return Math.max(0, (this.chart.dates || []).length - 1);
    },
    sliderIndex() {
      return this.hoverIndex === null ? this.lastIndex : this.hoverIndex;
    },
    sliderText() {
      return hoverSummary(this.hover) || this.ariaLabel;
    },
    ariaLabel() {
      const names = this.chart.series.map((item) => item.label + (item.lastText ? " (último " + item.lastText + ")" : ""));
      return (this.title ? this.title + ": " : "Gráfico de linha: ") + names.join(", ");
    },
  },
  watch: {
    series() {
      this.hoverIndex = null;
    },
    hover() {
      this.$nextTick(this.placeTip);
    },
  },
  mounted() {
    this.measure();
    if (typeof window !== "undefined") window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    if (typeof window !== "undefined") window.removeEventListener("resize", this.onResize);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  },
  methods: {
    measure() {
      const width = this.$el && this.$el.clientWidth;
      if (width > 0) this.measuredWidth = width;
    },
    onResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.measure, RESIZE_DELAY_MS);
    },
    pointAt(clientX) {
      const svg = this.$refs.svg;
      if (!svg || typeof svg.getBoundingClientRect !== "function") return;
      const rect = svg.getBoundingClientRect();
      if (!rect.width) return;
      const index = nearestIndex(this.chart, ((clientX - rect.left) / rect.width) * this.chart.width);
      this.hoverIndex = index === -1 ? null : index;
    },
    onPointer(event) {
      this.pointAt(event.clientX);
    },
    onTouch(event) {
      const touch = event.touches && event.touches[0];
      if (touch) this.pointAt(touch.clientX);
    },
    onFocus() {
      const count = (this.chart.dates || []).length;
      if (this.hoverIndex === null && count) this.hoverIndex = count - 1;
    },
    onKey(event) {
      const count = (this.chart.dates || []).length;
      if (!count) return;
      const current = this.hoverIndex === null ? count - 1 : this.hoverIndex;
      const moves = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: count - 1 };
      if (event.key === "Escape") {
        this.clear();
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(moves, event.key)) return;
      event.preventDefault();
      this.hoverIndex = Math.max(0, Math.min(count - 1, moves[event.key]));
    },
    clear() {
      this.hoverIndex = null;
    },
    placeTip() {
      const plot = this.$refs.plot;
      const tip = this.$refs.tip;
      if (!plot || !tip || !this.hover || !this.chart.width) return;
      const boxWidth = plot.clientWidth;
      const placement = tooltipPlacement((this.hover.x / this.chart.width) * boxWidth, tip.offsetWidth, boxWidth, TIP_GAP_PX);
      const svg = this.$refs.svg;
      this.tipLeft = placement.left;
      this.tipTop = placement.below && svg ? svg.clientHeight + 4 : null;
    },
  },
};
</script>

<style scoped>
.pi-line {
  margin: 0 0 16px;
}

.pi-line__title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #3a424e;
}

.pi-line__plot {
  position: relative;
  outline: none;
}

.pi-line__plot:focus-visible {
  box-shadow: 0 0 0 2px #1b59f8;
  border-radius: 4px;
}

.pi-line__svg {
  display: block;
  width: 100%;
  height: auto;
}

.pi-line__weekend {
  fill: #6b7280;
  fill-opacity: 0.1;
}

.pi-line__hatch {
  stroke: #e2e5ea;
  stroke-width: 3;
}

.pi-line__coverage-label {
  fill: #8a929c;
  font-size: 10px;
}

.pi-line__grid line {
  stroke: #eceef1;
  stroke-width: 1;
}

.pi-line__axis text {
  fill: #6b7280;
  font-size: 11px;
}

.pi-line__area {
  fill-opacity: 0.1;
}

.pi-line__stroke {
  stroke-width: 2;
  stroke-linejoin: round;
}

.pi-line__dot {
  stroke-width: 1.5;
}

.pi-line__guide {
  stroke: #9aa3ae;
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.pi-line__focus-dot {
  stroke: #ffffff;
  stroke-width: 2;
}

.pi-line__tip {
  position: absolute;
  top: 4px;
  z-index: 5;
  width: max-content;
  min-width: 230px;
  padding: 8px 10px;
  border: 1px solid #d9dde3;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  font-size: 12px;
  color: #2f3742;
  pointer-events: none;
  white-space: nowrap;
}

.pi-line__tip-head {
  margin-bottom: 6px;
  font-weight: 700;
}

.pi-line__tip-table {
  border-collapse: collapse;
}

.pi-line__tip-table th {
  padding: 0 0 2px 10px;
  font-size: 10px;
  font-weight: 600;
  color: #8a929c;
  text-transform: uppercase;
}

.pi-line__tip-table td {
  padding: 2px 0 2px 10px;
}

.pi-line__tip-table .pi-line__tip-name {
  padding-left: 0;
}

.pi-line__tip-table .is-num {
  text-align: right;
}

.pi-line__tip-swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
}

.pi-line__tip-value {
  font-weight: 700;
}

.pi-line__tip-value.is-muted {
  font-weight: 400;
  color: #9aa3ae;
}

.pi-line__tip-delta.is-good {
  color: #12803c;
}

.pi-line__tip-delta.is-bad {
  color: #c0392b;
}

.pi-line__tip-delta.is-flat {
  color: #8a929c;
}

.pi-line__tip-extras,
.pi-line__tip-flags {
  max-width: 340px;
  margin: 6px 0 0;
  padding: 6px 0 0;
  border-top: 1px solid #eceff3;
  list-style: none;
  white-space: normal;
}

.pi-line__tip-label {
  color: #6b7280;
}

.pi-line__tip-flags li {
  color: #6b7280;
}

.pi-line__tip-flags li.is-coverage,
.pi-line__tip-flags li.is-partial,
.pi-line__tip-flags li.is-window {
  color: #9a6700;
}

.pi-line__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #3a424e;
}

.pi-line__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pi-line__last {
  color: #6b7280;
}

.pi-line__key {
  display: inline-block;
  width: 14px;
  height: 10px;
  border: 1px solid #d9dde3;
}

.pi-line__key--weekend {
  background: rgba(107, 114, 128, 0.1);
}

.pi-line__key--coverage {
  background: repeating-linear-gradient(45deg, #e2e5ea 0, #e2e5ea 2px, #ffffff 2px, #ffffff 5px);
}

.pi-line__empty {
  margin: 0;
  padding: 24px 0;
  font-size: 13px;
  color: #6b7280;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piLineChart.vue